import type { InsightBenchmarks } from '../../utils/overviewInsightBenchmarks.js';
import { parseInsightBenchmarkOverrides } from '../../utils/overviewInsightBenchmarks.js';
import {
  DEFAULT_VOUCHER_PLATFORM_DEFAULTS,
  parseVoucherFeatureSettings,
  parseVoucherPlatformDefaults,
  type VoucherFeatureSettings,
  type VoucherPlatformDefaults,
} from '../../utils/voucherSettings.js';
import {
  DEFAULT_COMPANY_PLACES_SEARCH_SCOPE,
  parseCompanyPlacesSearchScope,
  resolveCompanyPlacesSearchScope,
  type CompanyPlacesSearchScope,
} from '../../utils/companyPlacesSearch.js';
import {
  DEFAULT_ENQUIRY_CARD_QUOTE_ACTIONS,
  parseEnquiryCardQuoteActions,
  resolveEnquiryCardQuoteActions,
  type EnquiryCardQuoteActions,
} from '../../utils/enquiryCardQuoteActions.js';

export interface OrgSettingsPhoneNormalization {
  defaultCountryCode: string;
  indiaAutoPrefix: boolean;
  stripLeadingZero: boolean;
}

export interface OrgSettingsTransportRules {
  enabled: boolean;
  perKmRate: number;
  freeKm: number;
  currency: string;
}

/** Clamp currency fraction digits to a safe 0–4 range for Intl.NumberFormat. */
export function clampCurrencyDecimalPlaces(raw: unknown, fallback = 2): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(4, Math.max(0, Math.round(n)));
}

export interface PlatformBusinessDefaults {
  defaultCurrency: string;
  defaultTimezone: string;
  defaultPhoneCountryCode: string;
  defaultDateFormat: string;
  crmDateLocale: string;
  /**
   * Max fraction digits for money display (0–4). Org override:
   * `organizations.settings.currency_decimal_places`.
   */
  defaultCurrencyDecimalPlaces: number;
  /** Minimum add-on-only booking total (super-admin platform default). */
  defaultAddonMinimumOrder: number;
  /** Platform quote terms template when org company_settings terms are empty. Use `{company}` for org name. */
  defaultQuoteTerms: string;
  /** Platform payment terms template when org company_settings payment_terms are empty. */
  defaultQuotePaymentTerms: string;
  /** Optional overrides for admin overview insight KPI thresholds (currency-aware base + these). */
  insightBenchmarks: Partial<InsightBenchmarks>;
  /**
   * Inclusive YYYY-MM-DD from which overview / reports count payments and expenses.
   * Empty means “use org override, else TB fallback / all history”.
   */
  defaultBankStatementStartDate: string;
  /**
   * Optional monthly booked target for SaaS workspaces that have not set their own.
   * 0 means no platform default — never inherit Tools & Bubbles `mtd_revenue_target`.
   */
  defaultMtdRevenueTarget: number;
  /** Optional Schema.org LocalBusiness priceRange override (e.g. $$ or ₹₹). */
  defaultSchemaPriceRange: string;
  /** Domain for guest_* placeholder auth emails (SaaS multi-tenant). */
  placeholderGuestEmailDomain: string;
  /** Domain for phone-based placeholder auth emails. */
  placeholderPhoneEmailDomain: string;
  phoneNormalization: OrgSettingsPhoneNormalization;
  transport: OrgSettingsTransportRules;
  vouchers: VoucherPlatformDefaults;
  /**
   * Company (Google Places establishment) search. `country` lists businesses
   * across the org locale country (e.g. all of India). `service_area` keeps the
   * office radius bias used for address/area fields.
   */
  companyPlacesSearchScope: CompanyPlacesSearchScope;
  enquiryCardQuoteActions: EnquiryCardQuoteActions;
}

export const DEFAULT_PLATFORM_BUSINESS_DEFAULTS: PlatformBusinessDefaults = {
  defaultCurrency: 'USD',
  defaultTimezone: 'UTC',
  defaultPhoneCountryCode: '+1',
  defaultDateFormat: 'MM/DD/YYYY',
  crmDateLocale: 'en-GB',
  defaultCurrencyDecimalPlaces: 2,
  defaultAddonMinimumOrder: 4500,
  defaultQuoteTerms: '',
  defaultQuotePaymentTerms: '',
  insightBenchmarks: {},
  defaultBankStatementStartDate: '',
  defaultMtdRevenueTarget: 0,
  defaultSchemaPriceRange: '',
  placeholderGuestEmailDomain: 'guest.serviceapp.ai',
  placeholderPhoneEmailDomain: 'phone.serviceapp.ai',
  phoneNormalization: {
    defaultCountryCode: '+1',
    indiaAutoPrefix: false,
    stripLeadingZero: false,
  },
  transport: {
    enabled: false,
    perKmRate: 0,
    freeKm: 0,
    currency: 'USD',
  },
  vouchers: { ...DEFAULT_VOUCHER_PLATFORM_DEFAULTS },
  companyPlacesSearchScope: DEFAULT_COMPANY_PLACES_SEARCH_SCOPE,
  enquiryCardQuoteActions: { ...DEFAULT_ENQUIRY_CARD_QUOTE_ACTIONS },
};

export interface CompanySettingsSnapshot {
  company_name?: string | null;
  logo_url?: string | null;
  tagline?: string | null;
  phone?: string | null;
  email?: string | null;
  timezone?: string | null;
  currency?: string | null;
  address_line1?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  country?: string | null;
}

export interface ResolvedOrgSettings {
  orgId: string;
  timezone: string;
  currency: string;
  /** Max fraction digits for `formatOrgMoney` (0–4). */
  currencyDecimalPlaces: number;
  dateFormat: string;
  phoneCountryCode: string;
  crmDateLocale: string;
  intlNumberLocale: string;
  /** Add-on-only / special-services minimum order (org override or platform default). */
  addonMinimumOrder: number;
  /** Per-org admin insight KPI overrides (`organizations.settings.insight_benchmarks`). */
  insightBenchmarkOverrides: Partial<InsightBenchmarks>;
  /** Inclusive YYYY-MM-DD books start; empty inherits platform / TB fallback. */
  bankStatementStartDate: string;
  phoneNormalization: OrgSettingsPhoneNormalization;
  transport: OrgSettingsTransportRules;
  vouchers: VoucherFeatureSettings;
  companyPlacesSearchScope: CompanyPlacesSearchScope;
  enquiryCardQuoteActions: EnquiryCardQuoteActions;
  company: CompanySettingsSnapshot;
}

function num(raw: unknown, fallback = 0): number {
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
}

export function parsePlatformBusinessDefaults(raw: unknown): PlatformBusinessDefaults {
  if (!raw || typeof raw !== 'object') return DEFAULT_PLATFORM_BUSINESS_DEFAULTS;
  const o = raw as Record<string, unknown>;
  const phone = (o.phone_normalization as Record<string, unknown>) ?? {};
  const transport = (o.transport as Record<string, unknown>) ?? {};
  const base = DEFAULT_PLATFORM_BUSINESS_DEFAULTS;
  const currency = String(o.default_currency ?? base.defaultCurrency);
  return {
    defaultCurrency: currency,
    defaultTimezone: String(o.default_timezone ?? base.defaultTimezone),
    defaultPhoneCountryCode: String(o.default_phone_country_code ?? base.defaultPhoneCountryCode),
    defaultDateFormat: String(o.default_date_format ?? base.defaultDateFormat),
    crmDateLocale: String(o.crm_date_locale ?? base.crmDateLocale),
    defaultCurrencyDecimalPlaces: clampCurrencyDecimalPlaces(
      o.default_currency_decimal_places,
      base.defaultCurrencyDecimalPlaces,
    ),
    defaultAddonMinimumOrder: num(o.default_addon_minimum_order, base.defaultAddonMinimumOrder),
    defaultQuoteTerms: String(o.default_quote_terms ?? base.defaultQuoteTerms),
    defaultQuotePaymentTerms: String(o.default_quote_payment_terms ?? base.defaultQuotePaymentTerms),
    insightBenchmarks: parseInsightBenchmarkOverrides(o.insight_benchmarks),
    defaultBankStatementStartDate: String(o.default_bank_statement_start_date ?? '').trim(),
    defaultMtdRevenueTarget: Math.max(0, num(o.default_mtd_revenue_target, base.defaultMtdRevenueTarget)),
    defaultSchemaPriceRange: String(o.default_schema_price_range ?? base.defaultSchemaPriceRange),
    placeholderGuestEmailDomain: String(o.placeholder_guest_email_domain ?? base.placeholderGuestEmailDomain),
    placeholderPhoneEmailDomain: String(o.placeholder_phone_email_domain ?? base.placeholderPhoneEmailDomain),
    phoneNormalization: {
      defaultCountryCode: String(phone.default_country_code ?? base.phoneNormalization.defaultCountryCode),
      indiaAutoPrefix: phone.india_auto_prefix === true,
      stripLeadingZero: phone.strip_leading_zero === true,
    },
    transport: {
      enabled: transport.enabled === true,
      perKmRate: num(transport.per_km_rate, base.transport.perKmRate),
      freeKm: num(transport.free_km, base.transport.freeKm),
      currency: String(transport.currency ?? currency),
    },
    vouchers: parseVoucherPlatformDefaults(o.vouchers),
    companyPlacesSearchScope: parseCompanyPlacesSearchScope(
      o.company_places_search_scope,
      base.companyPlacesSearchScope,
    ),
    enquiryCardQuoteActions: parseEnquiryCardQuoteActions(
      o.enquiry_card_quote_actions,
      base.enquiryCardQuoteActions,
    ),
  };
}

function intlLocaleForCurrency(currency: string, country?: string): string {
  if (country === 'IN' || currency === 'INR') return 'en-IN';
  if (country === 'AE' || currency === 'AED') return 'en-AE';
  if (country === 'SA' || currency === 'SAR') return 'en-SA';
  if (country === 'US' || currency === 'USD') return 'en-US';
  if (country === 'GB' || currency === 'GBP') return 'en-GB';
  if (country === 'AU' || currency === 'AUD') return 'en-AU';
  if (country && /^[A-Z]{2}$/.test(country)) return `en-${country}`;
  return 'en-US';
}

/**
 * Display currency for an existing workspace.
 * Company profile, then org locale. Never platform defaultCurrency (USD) —
 * that flash is what made Tools & Bubbles payments/invoices/booking show $.
 */
export function resolveOrgDisplayCurrency(
  companyCurrency?: string | null,
  orgLocaleCurrency?: string | null,
): string {
  const fromCompany = String(companyCurrency ?? '').trim();
  if (fromCompany) return fromCompany;
  return String(orgLocaleCurrency ?? '').trim();
}

export function resolveOrgSettings(input: {
  orgId: string;
  orgLocale?: {
    country?: string;
    timezone?: string;
    currency?: string;
    phone_country_code?: string;
    date_format?: string;
  } | null;
  orgSettings?: Record<string, unknown> | null;
  companySettings?: CompanySettingsSnapshot | null;
  platform?: PlatformBusinessDefaults;
}): ResolvedOrgSettings {
  const platform = input.platform ?? DEFAULT_PLATFORM_BUSINESS_DEFAULTS;
  const orgLocale = input.orgLocale ?? {};
  const company = input.companySettings ?? {};
  const orgCustom = input.orgSettings ?? {};
  const transportOverride = (orgCustom.transport as Record<string, unknown>) ?? {};

  const currency = resolveOrgDisplayCurrency(company.currency, orgLocale.currency);
  const timezone =
    String(company.timezone ?? orgLocale.timezone ?? platform.defaultTimezone).trim() ||
    platform.defaultTimezone;
  const dateFormat =
    String(orgLocale.date_format ?? platform.defaultDateFormat).trim() || platform.defaultDateFormat;
  const phoneCountryCode =
    String(orgLocale.phone_country_code ?? platform.defaultPhoneCountryCode).trim() ||
    platform.defaultPhoneCountryCode;

  const transport: OrgSettingsTransportRules = {
    enabled:
      transportOverride.enabled === true || (transportOverride.enabled == null && platform.transport.enabled),
    perKmRate: num(transportOverride.per_km_rate, platform.transport.perKmRate),
    freeKm: num(transportOverride.free_km, platform.transport.freeKm),
    currency: String(transportOverride.currency ?? currency),
  };

  const pricingOverride = (orgCustom.pricing as Record<string, unknown>) ?? {};
  const addonMinimumOrder = num(
    orgCustom.addon_minimum_order ?? pricingOverride.addon_minimum_order,
    platform.defaultAddonMinimumOrder,
  );

  const currencyDecimalPlaces = clampCurrencyDecimalPlaces(
    orgCustom.currency_decimal_places ?? pricingOverride.currency_decimal_places,
    platform.defaultCurrencyDecimalPlaces,
  );

  const orgInsightBenchmarks = parseInsightBenchmarkOverrides(
    orgCustom.insight_benchmarks ?? pricingOverride.insight_benchmarks,
  );

  const bankStatementStartDate = String(
    orgCustom.bank_statement_start_date || platform.defaultBankStatementStartDate || '',
  ).trim();

  return {
    orgId: input.orgId,
    timezone,
    currency,
    currencyDecimalPlaces,
    dateFormat,
    phoneCountryCode,
    crmDateLocale: platform.crmDateLocale,
    intlNumberLocale: intlLocaleForCurrency(currency, orgLocale.country),
    addonMinimumOrder,
    insightBenchmarkOverrides: orgInsightBenchmarks,
    bankStatementStartDate,
    phoneNormalization: { ...platform.phoneNormalization },
    transport,
    vouchers: parseVoucherFeatureSettings(orgCustom, platform.vouchers),
    companyPlacesSearchScope: resolveCompanyPlacesSearchScope({
      orgSettings: orgCustom,
      platformScope: platform.companyPlacesSearchScope,
    }),
    enquiryCardQuoteActions: resolveEnquiryCardQuoteActions({
      orgSettings: orgCustom,
      platform: platform.enquiryCardQuoteActions,
    }),
    company: {
      company_name: company.company_name,
      logo_url: company.logo_url,
      tagline: company.tagline,
      phone: company.phone,
      email: company.email,
      timezone,
      currency,
      address_line1: company.address_line1,
      city: company.city,
      state: company.state,
      pincode: company.pincode,
      country: company.country,
    },
  };
}

/** Normalize customer phone using org/platform rules (replaces scattered +91 logic in SaaS paths). */
export function normalizePhoneWithOrgRules(
  raw: string,
  settings: Pick<ResolvedOrgSettings, 'phoneCountryCode' | 'phoneNormalization'>,
): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw.trim();

  if (raw.trim().startsWith('+')) return raw.trim();

  const { phoneCountryCode, phoneNormalization } = settings;
  if (phoneNormalization.indiaAutoPrefix && digits.length === 10 && /^[6-9]/.test(digits)) {
    return `+91${digits}`;
  }

  const codeDigits = phoneCountryCode.replace(/\D/g, '');
  if (phoneNormalization.stripLeadingZero && digits.startsWith('0')) {
    return `+${codeDigits}${digits.slice(1)}`;
  }

  if (digits.length === 10 && codeDigits) {
    return `+${codeDigits}${digits}`;
  }

  return raw.trim().startsWith('+') ? raw.trim() : `+${digits}`;
}

export function formatOrgMoney(amount: number, settings: ResolvedOrgSettings): string {
  const maxFrac = clampCurrencyDecimalPlaces(settings.currencyDecimalPlaces, 2);
  if (!settings.currency) {
    return new Intl.NumberFormat(settings.intlNumberLocale || undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxFrac,
    }).format(amount);
  }
  return new Intl.NumberFormat(settings.intlNumberLocale, {
    style: 'currency',
    currency: settings.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFrac,
  }).format(amount);
}

export function formatOrgDate(
  value: Date | string | number,
  settings: ResolvedOrgSettings,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(settings.crmDateLocale, {
    timeZone: settings.timezone,
    ...options,
  });
}
